import React, { useMemo, useCallback, useState } from "react";
import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Toast, ToastContainer } from "../../../components/CustomUI/Toast";
import ApprovalInbox from "../../../components/CustomUI/ApprovalInbox";
import { useAuth } from "../../../services/useAuth";
import { hasPageAccessForUser } from "../../../config/roles";
import {
  APPROVES_PAGE_PATH,
  getReadableApprovalEntityTypes,
  hasApprovalEntityWrite,
} from "../../../config/approvalAccess";
import { useTitle } from "../../../hooks/useTitle";
import { useApprovalInbox } from "../../../hooks/useApprovalInbox";
import AccessNotice from "../../../components/CustomUI/AccessNotice";
import { useApprovalFilters } from "./useApprovalFilters";
import {
  buildAdaptiveMultiFilterSection,
  buildRadioFilterSection,
} from "../shared/buildListFilterSections";
import { approvalPlugins } from "../../../approvals/registry";

const STATUS_OPTIONS = [
  { value: "all", label: "Tümü" },
  { value: "pending", label: "Bekliyor" },
  { value: "approved", label: "Onaylandı" },
  { value: "rejected", label: "Reddedildi" },
];

const PermissionApproves = () => {
  useTitle("Rovion MES - Onaylar");
  const { getUser } = useAuth();
  const currentUser = getUser();

  const canViewPage =
    !!currentUser && hasPageAccessForUser(currentUser, APPROVES_PAGE_PATH);

  const allowedEntityTypes = useMemo(
    () => (currentUser ? getReadableApprovalEntityTypes(currentUser) : []),
    [currentUser],
  );

  const canWriteEntity = useCallback(
    (entityType) =>
      !!currentUser && hasApprovalEntityWrite(currentUser, entityType),
    [currentUser],
  );

  const [toasts, setToasts] = useState([]);
  const { filters, apply, listMode, queueType, queueParams, allTypesSelected } =
    useApprovalFilters(allowedEntityTypes);

  const stage = "admin";

  const addToast = useCallback((description, appearance = "success") => {
    setToasts([
      {
        description,
        id: Date.now(),
        title: appearance === "success" ? "Başarılı!" : "Hata!",
        appearance,
      },
    ]);
  }, []);

  const inbox = useApprovalInbox({
    stage,
    canWriteEntity,
    approvalUser: currentUser,
    listMode,
    queueType,
    queueParams,
    onNotify: addToast,
  });

  const typeOptions = useMemo(
    () =>
      Object.entries(approvalPlugins)
        .filter(([value]) => allowedEntityTypes.includes(value))
        .map(([value, plugin]) => ({
          value,
          label: plugin.typeLabel || value,
        })),
    [allowedEntityTypes],
  );

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (!allTypesSelected) n += 1;
    if (filters.status !== "all") n += 1;
    return n;
  }, [allTypesSelected, filters.status]);

  const approvalFilterSections = useMemo(
    () => [
      buildAdaptiveMultiFilterSection({
        id: "types",
        title: "Tür",
        selectedIds: filters.entityTypes,
        onChange: filters.setEntityTypes,
        options: typeOptions,
        showAllOption: false,
        searchPlaceholder: "Tür ara…",
      }),
      buildRadioFilterSection({
        id: "status",
        title: "Durum",
        value: filters.status,
        onChange: (v) => filters.setStatus(String(v ?? "pending")),
        options: STATUS_OPTIONS,
      }),
    ],
    [filters.entityTypes, filters.setEntityTypes, filters.status, filters.setStatus, typeOptions],
  );

  const handleApply = useCallback(() => {
    apply();
    inbox.setViewItem(null);
  }, [apply, inbox]);

  const handleReset = useCallback(() => {
    filters.reset();
    inbox.setViewItem(null);
  }, [filters, inbox]);

  const breadcrumbs = [{ label: "Rovion", url: "" }, { label: "Onaylar" }];

  if (!canViewPage) {
    return (
      <AppPage title="Onaylar" breadcrumbs={breadcrumbs}>
        <AccessNotice preset="no-page" />
      </AppPage>
    );
  }

  if (allowedEntityTypes.length === 0) {
    return (
      <AppPage title="Onaylar" breadcrumbs={breadcrumbs}>
        <AccessNotice preset="no-types" />
      </AppPage>
    );
  }

  return (
    <AppPage
      title="Onaylar"
      breadcrumbs={breadcrumbs}
    >
      <Container>
        <ContainerItem>
          <div className="card shadow-sm">
            <div className="card-body">
              <ApprovalInbox
                stage={stage}
                canWriteEntity={canWriteEntity}
                listMode={listMode}
                inbox={inbox}
                onNotify={addToast}
                filterSections={approvalFilterSections}
                activeFilterCount={activeFilterCount}
                onApplyFilters={handleApply}
                onResetFilters={handleReset}
              />
            </div>
          </div>
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts.map((t, i) => (
          <Toast
            key={t.id}
            {...t}
            onClose={() => setToasts((prev) => prev.filter((_, j) => j !== i))}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default PermissionApproves;
